import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUnitsBySubject, addUnit, updateUnit, deleteUnit } from '../../services/operations/unitAPI';
import SEO from '../../components/SEO';
import {
  FileText,
  Plus,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  BookOpen,
  Pencil,
  Trash2,
  X,
  Save
} from 'lucide-react';

// Rupee text-icon for currency display
const RupeeIcon = ({ className = '' }) => (
  <span className={`inline-flex items-center justify-center ${className}`}>₹</span>
);

const UnitCard = ({ unit, onEdit, onDelete }) => {
  const buildDriveUrl = (value) => {
    if (!value) return '#';
    const raw = String(value).trim();
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://drive.google.com/file/d/${raw}/view`;
  };

  const targetUrl = buildDriveUrl(unit.googleDriveId || unit.pdfUrl);

  const openLink = (e) => {
    if (e.target.tagName === 'A' || e.target.closest('a') || e.target.closest('button')) return;
    if (!targetUrl || targetUrl === '#') return;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openLink(e);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openLink}
      onKeyDown={onKeyDown}
      className="group cursor-pointer relative p-5 bg-gradient-to-br from-gray-800/40 to-gray-900/40 hover:from-gray-800/60 hover:to-gray-900/60 border border-gray-700/50 hover:border-indigo-500/50 rounded-xl shadow-lg hover:shadow-xl hover:shadow-indigo-500/10 transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-between gap-4 min-h-[120px] overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/5 group-hover:to-purple-500/5 transition-all duration-300" />
      <div className="relative flex items-center gap-4 min-w-0 flex-1">
        <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex-shrink-0">
          <FileText className="w-6 h-6 text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base text-white group-hover:text-indigo-300 transition-colors truncate mb-1">{unit.title || unit.name}</div>
          <div className="text-sm text-gray-400 flex items-center gap-1.5">
            <RupeeIcon className="w-4 h-4" />
            <span>{unit.price ?? 'N/A'}</span>
          </div>
        </div>
      </div>

      <div className="relative flex items-center gap-2 flex-shrink-0">
        <a
          href={targetUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="px-4 py-2 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 text-sm text-indigo-300 border border-indigo-500/30 flex items-center gap-2 transition-all"
        >
          <ExternalLink className="w-4 h-4" />
          Open
        </a>
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(unit); }}
          className="p-2 rounded-lg bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 border border-yellow-500/20 transition-all"
          title="Edit unit"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(unit); }}
          className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all"
          title="Delete unit"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Local Notification component for consistency with Branches page
const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  const baseClasses = 'fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg font-medium transition-all duration-300 transform backdrop-blur-sm border';
  const typeClasses = type === 'error' 
    ? 'bg-red-500/10 border-red-500/30 text-red-400' 
    : 'bg-green-500/10 border-green-500/30 text-green-400';
  return (
    <div className={`${baseClasses} ${typeClasses}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {type === 'error' ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <CheckCircle2 className="w-5 h-5" />
          )}
          <span>{message}</span>
        </div>
        <button onClick={onClose} className="ml-4 text-white/80 hover:text-white transition">&times;</button>
      </div>
    </div>
  );
};


const Units = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ message: null, type: null });
  const [newUnit, setNewUnit] = useState({ title: '', price: '', googleDriveId: '' });
  const [addingUnit, setAddingUnit] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', price: '', googleDriveId: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingUnit, setDeletingUnit] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getUnitsBySubject(subjectId);
        if (res && res.success) setUnits(res.units || res.unit || []);
        else throw new Error(res?.message || 'Could not fetch units');
      } catch (err) {
        console.error(err);
        setError(err.message || 'Error fetching units');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [subjectId]);

  const handleNewUnitChange = (e) => {
    const { name, value } = e.target;
    setNewUnit(prev => ({ ...prev, [name]: value }));
  };

  // Try to extract Google Drive file id from a variety of URL formats
  const extractDriveId = (input) => {
    if (!input) return '';
    // If it's already a plain id (no slashes, short-ish), return it
    const maybeId = input.trim();
    if (!maybeId.includes('http') && !maybeId.includes('/')) return maybeId;

    // Patterns to match common Drive URLs
    const patterns = [
      /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/, // /file/d/ID
      /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/, // open?id=ID
      /drive\.google\.com\/uc\?id=([a-zA-Z0-9_-]+)/, // uc?id=ID
      /docs\.google\.com\/.*\/d\/([a-zA-Z0-9_-]+)/, // docs/.../d/ID
      /id=([a-zA-Z0-9_-]{10,})/ // generic id=ID
    ];

    for (const re of patterns) {
      const m = maybeId.match(re);
      if (m && m[1]) return m[1];
    }

    // Fallback: return the original input (could be a public URL)
    return maybeId;
  };

  const handleAddUnit = async (e) => {
    e.preventDefault();
    if (!newUnit.title) return setNotification({ message: 'Please provide a title', type: 'error' });
    if (!newUnit.price) return setNotification({ message: 'Please provide a price', type: 'error' });
    if (!newUnit.googleDriveId) return setNotification({ message: 'Please provide a Google Drive file id or URL', type: 'error' });
    setAddingUnit(true);
    try {
      // If user pasted a full URL, send the extracted id (server expects googleDriveId)
      const prepared = { ...newUnit };
      const extracted = extractDriveId(newUnit.googleDriveId);
      prepared.googleDriveId = extracted;

      const res = await addUnit(subjectId, prepared);
      if (res && res.success) {
        // prefer common property names returned by server
        const created = res.unit || res.unitCreated || res.unitData || res;
        setUnits(prev => [created, ...prev]);
        // reset form
        setNewUnit({ title: '', price: '', googleDriveId: '' });
        setNotification({ message: 'Unit added', type: 'success' });
      } else {
        const msg = res?.message || 'Failed to add unit';
        setNotification({ message: msg, type: 'error' });
        console.error('Add unit failed', res);
      }
    } catch (err) {
      console.error(err);
      setNotification({ message: 'Failed to add unit', type: 'error' });
    } finally {
      setAddingUnit(false);
    }
  };
  const openEditModal = (unit) => {
    setEditingUnit(unit);
    setEditForm({ title: unit.title || unit.name || '', price: unit.price != null ? String(unit.price) : '', googleDriveId: unit.googleDriveId || '' });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.title) return setNotification({ message: 'Title is required', type: 'error' });
    setSavingEdit(true);
    try {
      const prepared = { ...editForm };
      const extracted = extractDriveId(editForm.googleDriveId);
      prepared.googleDriveId = extracted;
      const res = await updateUnit(editingUnit._id || editingUnit.id, prepared);
      if (res && res.success) {
        setUnits(prev => prev.map(u => (u._id || u.id) === (editingUnit._id || editingUnit.id) ? res.unit : u));
        setEditingUnit(null);
        setNotification({ message: 'Unit updated', type: 'success' });
      } else {
        setNotification({ message: res?.message || 'Failed to update unit', type: 'error' });
      }
    } catch (err) {
      setNotification({ message: 'Failed to update unit', type: 'error' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeletingUnit(true);
    try {
      const res = await deleteUnit(deleteTarget._id || deleteTarget.id);
      if (res && res.success) {
        setUnits(prev => prev.filter(u => (u._id || u.id) !== (deleteTarget._id || deleteTarget.id)));
        setNotification({ message: 'Unit deleted', type: 'success' });
      } else {
        setNotification({ message: res?.message || 'Failed to delete unit', type: 'error' });
      }
    } catch (err) {
      setNotification({ message: 'Failed to delete unit', type: 'error' });
    } finally {
      setDeletingUnit(false);
      setDeleteTarget(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
        <p className="text-gray-300 text-lg font-medium">Loading units...</p>
      </div>
    </div>
  );

  return (
    <>
      <SEO
        title="Manage Units - Admin Panel | ReferMe"
        description="Admin panel for managing units and their Google Drive links."
        noindex={true}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
      <Notification message={notification.message} type={notification.type} onClose={() => setNotification({ message: null, type: null })} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <header className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-xl p-6 sm:p-8 mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                  <FileText className="w-6 h-6 text-indigo-400" />
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Units
                </h1>
              </div>
              <p className="text-sm sm:text-base text-gray-400 ml-12">
                Manage and browse units of the selected subject.
              </p>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button 
                onClick={() => navigate(-1)} 
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-gray-800/40 hover:bg-gray-800/60 text-white border border-gray-700/50 transition-all flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>
        </header>

        {/* Error Display */}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 backdrop-blur-sm p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Create new unit form */}
        <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 sm:p-8 mb-8 shadow-xl">
          <div className="flex items-center gap-2 mb-6">
            <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <Plus className="w-4 h-4 text-indigo-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Create New Unit</h3>
          </div>
          <form onSubmit={handleAddUnit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <BookOpen className="w-5 h-5" />
              </div>
              <input 
                name="title" 
                value={newUnit.title} 
                onChange={handleNewUnitChange} 
                placeholder="Unit title" 
                className="w-full p-3 pl-10 rounded-xl bg-gray-800/40 backdrop-blur-sm text-white border border-gray-700/50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 transition-all placeholder-gray-500" 
              />
            </div>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <RupeeIcon className="w-5 h-5" />
              </div>
              <input 
                name="price" 
                value={newUnit.price} 
                onChange={handleNewUnitChange} 
                placeholder="Price" 
                type="number"
                className="w-full p-3 pl-10 rounded-xl bg-gray-800/40 backdrop-blur-sm text-white border border-gray-700/50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 transition-all placeholder-gray-500" 
              />
            </div>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <FileText className="w-5 h-5" />
              </div>
              <input 
                name="googleDriveId" 
                value={newUnit.googleDriveId} 
                onChange={handleNewUnitChange} 
                placeholder="Google Drive file id or URL" 
                className="w-full p-3 pl-10 rounded-xl bg-gray-800/40 backdrop-blur-sm text-white border border-gray-700/50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 transition-all placeholder-gray-500" 
              />
            </div>
            <button 
              type="submit" 
              disabled={addingUnit} 
              className={`w-full px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:shadow-indigo-500/20 transition-all flex items-center justify-center gap-2 ${addingUnit ? 'bg-gray-600/50 text-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
            >
              {addingUnit ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Add Unit
                </>
              )}
            </button>
          </form>
        </div>

        {/* Units Grid */}
        <div className="grid grid-cols-1 gap-4">
          {units.length === 0 ? (
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-12 text-center">
              <div className="p-4 rounded-full bg-gray-800/30 border border-gray-700/50 w-fit mx-auto mb-4">
                <FileText className="w-12 h-12 text-gray-500" />
              </div>
              <p className="text-gray-400 text-sm">No units found for this subject</p>
            </div>
          ) : (
            units.map(u => <UnitCard key={u._id || u.id} unit={u} onEdit={openEditModal} onDelete={setDeleteTarget} />)
          )}
        </div>
      </section>
      </div>

      {/* Edit Modal */}
      {editingUnit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <Pencil className="w-4 h-4 text-yellow-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Edit Unit</h3>
              </div>
              <button onClick={() => setEditingUnit(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="flex flex-col gap-4">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <BookOpen className="w-5 h-5" />
                </div>
                <input name="title" value={editForm.title} onChange={handleEditChange} placeholder="Unit title" className="w-full p-3 pl-10 rounded-xl bg-gray-800/40 text-white border border-gray-700/50 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/30 transition-all placeholder-gray-500" />
              </div>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <RupeeIcon className="w-5 h-5" />
                </div>
                <input name="price" value={editForm.price} onChange={handleEditChange} placeholder="Price" type="number" className="w-full p-3 pl-10 rounded-xl bg-gray-800/40 text-white border border-gray-700/50 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/30 transition-all placeholder-gray-500" />
              </div>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <FileText className="w-5 h-5" />
                </div>
                <input name="googleDriveId" value={editForm.googleDriveId} onChange={handleEditChange} placeholder="Google Drive file id or URL" className="w-full p-3 pl-10 rounded-xl bg-gray-800/40 text-white border border-gray-700/50 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/30 transition-all placeholder-gray-500" />
              </div>
              <div className="flex gap-3 mt-2">
                <button type="button" onClick={() => setEditingUnit(null)} className="flex-1 px-4 py-3 rounded-xl bg-gray-800/60 hover:bg-gray-800 text-gray-300 border border-gray-700/50 transition-all font-medium">
                  Cancel
                </button>
                <button type="submit" disabled={savingEdit} className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${savingEdit ? 'bg-gray-600/50 text-gray-300 cursor-not-allowed' : 'bg-yellow-600 hover:bg-yellow-700 text-white'}`}>
                  {savingEdit ? <><Loader2 className="animate-spin h-5 w-5" />Saving...</> : <><Save className="w-5 h-5" />Save Changes</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl p-6 text-center">
            <div className="p-4 rounded-full bg-red-500/10 border border-red-500/20 w-fit mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Delete Unit</h3>
            <p className="text-gray-400 text-sm mb-6">Are you sure you want to delete <span className="text-white font-medium">{deleteTarget.title || deleteTarget.name}</span>? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-3 rounded-xl bg-gray-800/60 hover:bg-gray-800 text-gray-300 border border-gray-700/50 transition-all font-medium">
                Cancel
              </button>
              <button onClick={handleDeleteConfirm} disabled={deletingUnit} className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${deletingUnit ? 'bg-gray-600/50 text-gray-300 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
                {deletingUnit ? <><Loader2 className="animate-spin h-5 w-5" />Deleting...</> : <><Trash2 className="w-5 h-5" />Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Units;
